import React from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SectionHeader from '../OpenDoor/SectionHeader';
import CharCount from '../OpenDoor/CharCount';
import ProspectNameAutocomplete from './ProspectNameAutocomplete';
import { CHAR_LIMIT, FIELD_CHAR_LIMITS, textFieldSx } from './constants';

const HandleReplyContactSection = ({
  companyName,
  form,
  onFieldChange,
  onSelectProspect,
  onGenerate,
  isGenerating,
  canGenerate,
}) => (
  <Box
    border={1}
    borderColor='divider'
    borderRadius={2}
    p={2.5}
    display='flex'
    flexDirection='column'
    minHeight={{ md: 640 }}
  >
    <SectionHeader number={1} title='Tell me about your contact' />

    <ProspectNameAutocomplete
      value={form.fullName}
      onInputChange={(next) => onFieldChange('fullName')({ target: { value: next } })}
      onSelectProspect={onSelectProspect}
    />
    <TextField
      fullWidth
      size='small'
      label='Company'
      placeholder='Adidas'
      value={form.company}
      onChange={onFieldChange('company')}
      sx={textFieldSx}
    />
    <TextField
      fullWidth
      size='small'
      label='Title (optional)'
      placeholder='VP Digital Commerce'
      value={form.title}
      onChange={onFieldChange('title')}
      sx={textFieldSx}
    />
    <TextField
      fullWidth
      size='small'
      label='LinkedIn profile (optional)'
      placeholder='https://linkedin.com/in/…'
      value={form.linkedin}
      onChange={onFieldChange('linkedin')}
      InputProps={{
        endAdornment: (
          <InputAdornment position='end'>
            <LinkedInIcon sx={{ color: '#0a66c2', fontSize: 20 }} />
          </InputAdornment>
        ),
      }}
      sx={textFieldSx}
    />

    <Typography variant='body2' fontWeight={600} mb={0.75} mt={0.5}>
      How do you know this person?
    </Typography>
    <TextField
      fullWidth
      multiline
      minRows={2}
      placeholder='Met at a conference, introduced by a mutual contact, etc.'
      value={form.howDoYouKnowThisPerson}
      onChange={onFieldChange('howDoYouKnowThisPerson')}
      inputProps={{ maxLength: CHAR_LIMIT }}
      sx={{ ...textFieldSx, mb: 0 }}
    />
    <CharCount value={form.howDoYouKnowThisPerson} limit={CHAR_LIMIT} />

    <Typography variant='body2' fontWeight={600} mb={0.75} mt={1}>
      Why do you think {companyName} could be relevant?
    </Typography>
    <TextField
      fullWidth
      multiline
      minRows={2}
      placeholder={`Share why ${companyName} might help this prospect…`}
      value={form.whyClientRelevant}
      onChange={onFieldChange('whyClientRelevant')}
      inputProps={{ maxLength: CHAR_LIMIT }}
      sx={{ ...textFieldSx, mb: 0 }}
    />
    <CharCount value={form.whyClientRelevant} limit={CHAR_LIMIT} />

    <Typography variant='body2' fontWeight={600} mb={0.75} mt={1}>
      Anything else I should know?
    </Typography>
    <TextField
      fullWidth
      multiline
      minRows={2}
      placeholder='Context, timing, tone preferences…'
      value={form.anythingElse}
      onChange={onFieldChange('anythingElse')}
      inputProps={{ maxLength: CHAR_LIMIT }}
      sx={{ ...textFieldSx, mb: 0 }}
    />
    <CharCount value={form.anythingElse} limit={CHAR_LIMIT} />

    <Typography variant='body2' fontWeight={600} mb={0.75} mt={1}>
      Latest reply from prospect
    </Typography>
    <TextField
      fullWidth
      multiline
      minRows={3}
      placeholder='Paste the most recent message from your prospect…'
      value={form.latestProspectReply}
      onChange={onFieldChange('latestProspectReply')}
      inputProps={{ maxLength: FIELD_CHAR_LIMITS.latestProspectReply }}
      sx={{ ...textFieldSx, mb: 0 }}
    />
    <CharCount
      value={form.latestProspectReply}
      limit={FIELD_CHAR_LIMITS.latestProspectReply}
    />

    <Typography variant='body2' fontWeight={600} mb={0.75} mt={1}>
      Full email thread or relevant communication history
    </Typography>
    <TextField
      fullWidth
      multiline
      minRows={3}
      placeholder='Paste earlier emails or notes that help set context…'
      value={form.communicationHistory}
      onChange={onFieldChange('communicationHistory')}
      inputProps={{ maxLength: FIELD_CHAR_LIMITS.communicationHistory }}
      sx={{ ...textFieldSx, mb: 0 }}
    />
    <CharCount
      value={form.communicationHistory}
      limit={FIELD_CHAR_LIMITS.communicationHistory}
    />

    <Box mt='auto' pt={2}>
      <Button
        fullWidth
        variant='contained'
        disabled={!canGenerate || isGenerating}
        onClick={onGenerate}
        startIcon={<AutoAwesomeIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 1.5,
          py: 1.25,
          boxShadow: 'none',
        }}
      >
        {isGenerating ? 'Saving…' : 'Generate reply'}
      </Button>
      <Box display='flex' alignItems='flex-start' gap={1} mt={1.5}>
        <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.15 }} />
        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.5 }}>
          Your contact details are saved so you can quickly find this person again. The latest reply and communication history are not saved with the contact
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default HandleReplyContactSection;
